

"use client";

import React, { useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { PALETTE } from "../../lib/colorPallete";
import { useOutsideClick } from "../../hooks/useOutsideClick";
import { ExpandedCardViewProps } from "../../types/types";
import { GalleryView } from "./GalleryView";
import { DetailsView } from "./DetailsView";
import { CloseButton } from "../ui/CloseButton";
import { ProductHeader } from "./ProductHeader";
import { MobileTabNavigation } from "./MobileTabNavigation";
import { MobileContent } from "./MobileContent";
import { useExpandedCard } from "../../hooks/useExpanded";
import { useProductCalculations } from "../../hooks/useProductCalculation";
import { useSelectionEffects } from "../../hooks/useSelectionEffects";

const ExpandedCardView: React.FC<ExpandedCardViewProps> = ({
  product,
  onClose,
  cartItemToEdit,
}) => {
  const ref = useOutsideClick<HTMLDivElement>(onClose);
  const {
    selectedCustomizations,
    setSelectedCustomizations,
    currentImageIndex,
    setCurrentImageIndex,
    isPremiumEnabled,
    setIsPremiumEnabled,
    activeTab,
    setActiveTab,
    quantity,
    minQuantity,
    handleQuantityChange,
    handleSelectionChange,
    handleConfirm,
    handleBuyNow,
    isCartLoading,
    isWholesaleUser,
  } = useExpandedCard(product, cartItemToEdit, onClose);
  const {
    availabilityMap,
    priceBreakdown,
    premiumPackageData,
    availableColors,
    isSelectedColorOutOfStock,
    finalDimensions,
    imageList,
  } = useProductCalculations(
    product,
    selectedCustomizations,
    isPremiumEnabled,
    isWholesaleUser
  );
  useSelectionEffects(
    product,
    selectedCustomizations,
    setSelectedCustomizations,
    availabilityMap,
    availableColors,
    imageList,
    setCurrentImageIndex,
    handleSelectionChange
  );

  const hasAccessoriesCustomization = useMemo(
    () => product.customization?.some((cust) => cust.type === "Accessories"),
    [product.customization]
  );

  useEffect(() => {
    console.log(product); 
  })

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, pointerEvents: "none" }}
      transition={{ duration: 0.3 }}
      className="fixed inset-0 grid place-items-center z-50 p-4"
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      <motion.div
        onClick={(e) => e.stopPropagation()}
        layoutId={`card-${product._id}`}
        ref={ref}
        className="relative z-[51] w-full max-w-5xl max-h-[90vh] max-md:max-h-[75vh] flex flex-col md:flex-row rounded-2xl overflow-y-auto overflow-x-hidden shadow-2xl"
        style={{ backgroundColor: PALETTE.cream }}
      >
        <CloseButton onClose={onClose} />

        {}
        <div className="hidden md:flex md:w-1/2">
          <GalleryView
            product={product}
            imageList={imageList}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            selectedColorName={selectedCustomizations["Color"]}
            handleColorSelect={(name) => handleSelectionChange("Color", name)}
            availableColors={availableColors}
            selectedCustomizations={selectedCustomizations}
            quantity={quantity}
            isWholeSaleUser={isWholesaleUser}
          />
        </div>
        <div className="hidden md:flex md:w-1/2">
          <DetailsView
            product={product}
            cartItemToEdit={cartItemToEdit}
            availabilityMap={availabilityMap}
            selectedCustomizations={selectedCustomizations}
            handleCustomizationSelect={handleSelectionChange}
            hasAccessoriesCustomization={hasAccessoriesCustomization}
            premiumPackageData={premiumPackageData}
            isPremiumEnabled={isPremiumEnabled}
            setIsPremiumEnabled={setIsPremiumEnabled}
            handleConfirm={handleConfirm}
            handleBuyNow={handleBuyNow}
            isCartLoading={isCartLoading}
            priceBreakdown={priceBreakdown}
            isWholesaleUser={isWholesaleUser}
            finalDimensions={finalDimensions}
            quantity={quantity}
            onQuantityChange={handleQuantityChange}
            minQuantity={minQuantity}
            isSelectedColorOutOfStock={isSelectedColorOutOfStock}
          />
        </div>

        {}
        <div className="flex flex-col w-full h-full md:hidden">
          <ProductHeader product={product} />
          
          <MobileTabNavigation 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
          />

          <MobileContent
            activeTab={activeTab}
            product={product}
            cartItemToEdit={cartItemToEdit}
            availabilityMap={availabilityMap}
            selectedCustomizations={selectedCustomizations}
            handleCustomizationSelect={handleSelectionChange}
            hasAccessoriesCustomization={hasAccessoriesCustomization}
            premiumPackageData={premiumPackageData}
            isPremiumEnabled={isPremiumEnabled}
            setIsPremiumEnabled={setIsPremiumEnabled}
            handleConfirm={handleConfirm}
            handleBuyNow={handleBuyNow}
            isCartLoading={isCartLoading}
            priceBreakdown={priceBreakdown}
            isWholesaleUser={isWholesaleUser}
            finalDimensions={finalDimensions}
            quantity={quantity}
            onQuantityChange={handleQuantityChange}
            minQuantity={minQuantity}
            isSelectedColorOutOfStock={isSelectedColorOutOfStock}
            imageList={imageList}
            currentImageIndex={currentImageIndex}
            setCurrentImageIndex={setCurrentImageIndex}
            availableColors={availableColors}
          />
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ExpandedCardView;